/**
 * The service period an invoice prints — derived from its stored date range.
 *
 * Part of the record vocabulary rather than the document contract in
 * `invoice.ts`: it turns the half-open `[start, end)` range an invoice stores
 * into the phrase a customer reads.
 */

/**
 * The label an invoice prints for its service period.
 *
 * Derived, never stored. There is no hand-typed label on an invoice: the
 * machine-readable period is the half-open `service_period_start`/`_end` pair,
 * and this is the one function that turns it into the text a customer reads —
 * so the printed month and the range the invoice is reconciled against cannot
 * describe different periods.
 *
 * A range covering exactly one calendar month renders as that month ('July
 * 2026'), which is the overwhelmingly common case and matches the reference
 * invoices. A run of whole months renders as a span ('Jul–Sep 2026', 'Nov
 * 2026–Jan 2027') — the same wording {@link buildPeriod} gives the month-end
 * run, so a quarterly retainer reads the way it always has.
 *
 * Anything else prints both ends, INCLUSIVE — the stored range is half-open,
 * but a customer reading "Jul 1 - Aug 1" for July would reasonably think they
 * were billed for a day of August.
 *
 * Lives in `shared` because three places must produce the SAME text: the API
 * when it builds a document to render, the spreadsheet export, and the web
 * editor's live preview. A second implementation is a preview that disagrees
 * with the PDF it is previewing.
 */
/**
 * Convert a stored (EXCLUSIVE) service-period end to the inclusive last day a
 * human would name, and back.
 *
 * The column is half-open `[start, end)` — September is stored as
 * `2026-09-01 → 2026-10-01` — and that is load-bearing: it is why two
 * consecutive months meet exactly once, why a timesheet entry on the 1st
 * belongs to exactly one invoice, and what
 * `invoices_generated_sow_period_unique` keys on. None of that changes.
 *
 * What these are for is the EDITOR. A field labelled "Service period end"
 * showing `Oct 1` tells a finance user the period runs into October; nobody in
 * billing thinks of September as ending on the 1st of October. So the form
 * displays `Sep 30` and converts on the way in and out, leaving storage alone.
 *
 * Named as a pair, and kept beside {@link deriveServicePeriodLabel} which does
 * the same `-1 day` for the printed label, so the two cannot drift. Blank and
 * nullish pass through untouched: "no period" is a real state and must not
 * become a date.
 */
export function servicePeriodEndToInclusive(end: string | null | undefined): string {
  if (end == null || end === '') return '';
  return addDays(end, -1) ?? '';
}

/** Inverse of {@link servicePeriodEndToInclusive}: the inclusive last day a
 *  user picked becomes the exclusive end the column stores. */
export function servicePeriodEndFromInclusive(lastDay: string | null | undefined): string {
  if (lastDay == null || lastDay === '') return '';
  return addDays(lastDay, 1) ?? '';
}

export function deriveServicePeriodLabel(
  start: string | null | undefined,
  end: string | null | undefined,
): string | null {
  // `== null`, not `=== null`. These arrive from spreadsheet rows and jsonb,
  // where a column simply absent is `undefined` rather than null — and a
  // strict check let that through to `start.split(...)`.
  if (start == null || end == null) return null;

  const startParts = start.split('-');
  const endParts = end.split('-');
  const [sy, sm, sd] = startParts;
  const [ey, em, ed] = endParts;
  if (!sy || !sm || !sd || !ey || !em || !ed) return null;

  // Half-open, so the last day covered is the day before `end`.
  const lastCovered = addDays(end, -1);
  if (lastCovered === null) return null;

  // Whole months, start to finish: the 1st through a month end. Everything a
  // billing run produces is one of these.
  if (sd === '01' && isMonthEnd(lastCovered)) {
    const [ly, lm] = lastCovered.split('-') as [string, string, string];
    if (`${sy}-${sm}` === `${ly}-${lm}`) return `${MONTHS[Number(sm) - 1]} ${sy}`;
    const from = MONTHS[Number(sm) - 1]?.slice(0, 3);
    const to = MONTHS[Number(lm) - 1]?.slice(0, 3);
    // En dash and the year stated once, exactly as `buildPeriod` writes it.
    return sy === ly ? `${from}–${to} ${sy}` : `${from} ${sy}–${to} ${ly}`;
  }

  return `${formatDay(start)} - ${formatDay(lastCovered)}`;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** `'2026-07-31'` → `'Jul 31, 2026'`. Built from the string's own parts;
 *  `new Date(iso)` parses a bare date as UTC midnight and renders it a day
 *  early west of Greenwich. */
function formatDay(iso: string): string {
  const [y, m, d] = iso.split('-');
  if (!y || !m || !d) return iso;
  return `${MONTHS[Number(m) - 1]?.slice(0, 3) ?? m} ${Number(d)}, ${y}`;
}

/** Whether `iso` is the last day of its month. */
function isMonthEnd(iso: string): boolean {
  return addDays(iso, 1)?.slice(8, 10) === '01';
}

/** `YYYY-MM-DD` + n days, in UTC. */
function addDays(isoDate: string, days: number): string | null {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
